import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Search, UserCog, RotateCcw } from 'lucide-react';
import type { Lead, Passbook } from '@/shared/types';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Textarea } from '@/shared/ui/textarea';
import { maskCpf } from '@/shared/lib/input-masks';
import { searchLeadByCpf, setTierOverride, clearTierOverride } from '../api/use-tier-override';

interface TierOverrideSectionProps {
  idPassbook: string;
  passbook: Passbook;
}

export function TierOverrideSection({ idPassbook, passbook }: TierOverrideSectionProps) {
  const queryClient = useQueryClient();
  const [cpfInput, setCpfInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [found, setFound] = useState<Lead | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [selectedLevel, setSelectedLevel] = useState<string | null>(null);
  const [reason, setReason] = useState('');
  const [applying, setApplying] = useState(false);

  const tiers = passbook.tiers ?? [];

  const handleSearch = async () => {
    const digits = cpfInput.replace(/\D/g, '');
    if (digits.length < 3) {
      toast.error('Informe um CPF válido.');
      return;
    }
    setSearching(true);
    setNotFound(false);
    setFound(null);
    try {
      const lead = await searchLeadByCpf(idPassbook, cpfInput);
      if (!lead) {
        setNotFound(true);
        return;
      }
      setFound(lead);
      setSelectedLevel(lead.tierOverride?.level ?? lead.tier?.level ?? null);
      setReason('');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível buscar o cliente.');
    } finally {
      setSearching(false);
    }
  };

  const handleApply = async () => {
    if (!found?._id || !selectedLevel) return;
    setApplying(true);
    try {
      await setTierOverride(idPassbook, found._id, selectedLevel, reason);
      await queryClient.invalidateQueries({ queryKey: ['passbook', idPassbook] });
      const tier = tiers.find((t) => t.level === selectedLevel);
      setFound((prev) =>
        prev
          ? {
              ...prev,
              tier: tier ? { level: tier.level, label: tier.label } : prev.tier,
              tierOverride: { level: selectedLevel, reason },
            }
          : prev,
      );
      toast.success('Tier atualizado manualmente.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível aplicar o override.');
    } finally {
      setApplying(false);
    }
  };

  const handleClear = async () => {
    if (!found?._id) return;
    setApplying(true);
    try {
      await clearTierOverride(idPassbook, found._id);
      await queryClient.invalidateQueries({ queryKey: ['passbook', idPassbook] });
      setFound((prev) => (prev ? { ...prev, tierOverride: null } : prev));
      toast.success('Override removido — o tier volta a ser calculado pelo saldo.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Não foi possível remover o override.');
    } finally {
      setApplying(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <UserCog className="size-4" /> Upgrade / downgrade manual
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Busque um cliente por CPF para alterar o nível dele manualmente. O override some sozinho
          se você removê-lo — sem ele, o nível volta a ser calculado pelo saldo de pontos.
        </p>

        <div className="flex gap-2">
          <Input
            value={cpfInput}
            onChange={(e) => setCpfInput(maskCpf(e.target.value))}
            onKeyDown={(e) => e.key === 'Enter' && void handleSearch()}
            placeholder="000.000.000-00"
            className="max-w-xs"
          />
          <Button variant="secondary" onClick={() => void handleSearch()} loading={searching}>
            <Search className="size-4" /> Buscar
          </Button>
        </div>

        {notFound ? (
          <p className="text-sm text-destructive">Nenhum cliente encontrado com esse CPF.</p>
        ) : null}

        {found ? (
          <div className="space-y-4 rounded-xl border border-border/50 p-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-sm font-semibold">{found.profile?.name ?? '—'}</div>
                <div className="text-xs text-muted-foreground">
                  CPF {maskCpf(found.profile?.uniqueId ?? '')}
                  {found.tier ? ` · nível atual: ${found.tier.label}` : ''}
                </div>
              </div>
              {found.tierOverride?.level ? (
                <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                  Override manual ativo
                </span>
              ) : null}
            </div>

            <div>
              <Label className="mb-2 block text-xs text-muted-foreground">Novo nível</Label>
              <div className="flex flex-wrap gap-2">
                {tiers.map((t) => (
                  <button
                    key={t.level}
                    type="button"
                    onClick={() => setSelectedLevel(t.level)}
                    className="flex items-center gap-2 rounded-lg border px-3 py-2 text-sm font-medium transition-colors"
                    style={{
                      borderColor: selectedLevel === t.level ? (t.color ?? undefined) : undefined,
                      backgroundColor:
                        selectedLevel === t.level ? `${t.color ?? '#9C764C'}1a` : undefined,
                    }}
                  >
                    <span
                      className="size-2.5 rounded-full"
                      style={{ backgroundColor: t.color || '#9C764C' }}
                    />
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-xs text-muted-foreground">Justificativa</Label>
              <Textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Motivo da alteração (fica registrado no cliente)"
                rows={2}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={() => void handleApply()}
                loading={applying}
                disabled={applying || !selectedLevel}
              >
                Aplicar alteração
              </Button>
              {found.tierOverride?.level ? (
                <Button variant="outline" onClick={() => void handleClear()} disabled={applying}>
                  <RotateCcw className="size-4" /> Voltar ao automático
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
